import { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRepeat, faSpinner } from '@/fontawesome.ts';
import Modal from '@/components/ui/Modal.tsx';
import EventFeatureFilterEditor, { buildEventFeatureFilter } from '@/components/admin/events/EventFeatureFilterEditor.tsx';
import RecurrenceRuleEditor, { DEFAULT_RECURRENCE_RULE } from '@/components/admin/events/RecurrenceRuleEditor.tsx';
import ReminderMinutesEditor from '@/components/admin/events/ReminderMinutesEditor.tsx';
import { APIRoutes } from '@/services/api.routes.ts';
import { showErrorToast } from '@/utils/toasts.ts';
import { getUserFacingError } from '@/utils/errorMessages';
import { reminderRowsToMinutes, toRFC3339, validateRecurrenceRule } from '@/utils/event-helpers';
import type { ReminderMinutesRow } from '@/utils/event-helpers';
import type { EventRecurrenceRule, EventSeries, Event, FeatureType } from '@/models';

interface CreateEventSeriesModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSeriesCreated: (series: EventSeries, events: Event[]) => void;
}

export default function CreateEventSeriesModal({ isOpen, onClose, onSeriesCreated }: CreateEventSeriesModalProps) {
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [startDate, setStartDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [until, setUntil] = useState('');
  const [link, setLink] = useState('');
  const [rule, setRule] = useState<EventRecurrenceRule>(DEFAULT_RECURRENCE_RULE);
  const [isPublic, setIsPublic] = useState(false);
  const [features, setFeatures] = useState<FeatureType[]>([]);
  const [excludeCommunityMembers, setExcludeCommunityMembers] = useState(false);
  const [onlyCommunityMembers, setOnlyCommunityMembers] = useState(false);
  const [reminderRows, setReminderRows] = useState<ReminderMinutesRow[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      const now = new Date();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      setStartDate(`${now.getFullYear()}-${month}-${day}`);
      setStartTime('18:00');
      setEndTime('19:00');

      // default the series to half a year, which stays far below the 500-occurrence cap
      const defaultUntil = new Date(now.getFullYear(), now.getMonth() + 6, now.getDate());
      const untilMonth = String(defaultUntil.getMonth() + 1).padStart(2, '0');
      const untilDay = String(defaultUntil.getDate()).padStart(2, '0');
      setUntil(`${defaultUntil.getFullYear()}-${untilMonth}-${untilDay}`);
    }
  }, [isOpen]);

  const resetForm = () => {
    setSubject('');
    setDescription('');
    setStartDate('');
    setStartTime('');
    setEndTime('');
    setUntil('');
    setLink('');
    setRule(DEFAULT_RECURRENCE_RULE);
    setIsPublic(false);
    setFeatures([]);
    setExcludeCommunityMembers(false);
    setOnlyCommunityMembers(false);
    setReminderRows([]);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!subject.trim()) {
      showErrorToast('Bitte geben Sie einen Betreff ein');
      return;
    }

    if (!startDate || !startTime || !endTime) {
      showErrorToast('Bitte geben Sie Startdatum, Start- und Endzeit ein');
      return;
    }

    if (!until) {
      showErrorToast('Bitte geben Sie ein Enddatum für die Serie ein');
      return;
    }

    if (!link.trim()) {
      showErrorToast('Bitte geben Sie einen Link ein');
      return;
    }

    if (!isPublic && features.length === 0) {
      showErrorToast('Bitte wählen Sie mindestens ein Feature aus oder markieren Sie die Serie als öffentlich');
      return;
    }

    const ruleError = validateRecurrenceRule(rule);
    if (ruleError !== null) {
      showErrorToast(ruleError);
      return;
    }

    const startISO = toRFC3339(startDate, startTime);
    const endISO = toRFC3339(startDate, endTime);
    if (startISO === null || endISO === null) {
      showErrorToast('Bitte geben Sie ein gültiges Datum und gültige Uhrzeiten ein');
      return;
    }

    // Mirrors validateEventSeriesWindow on the backend
    if (new Date(endISO) <= new Date(startISO)) {
      showErrorToast('Die Endzeit muss nach der Startzeit liegen');
      return;
    }

    if (until < startDate) {
      showErrorToast('Das Enddatum der Serie darf nicht vor dem Startdatum liegen');
      return;
    }

    const reminderResult = reminderRowsToMinutes(reminderRows);
    if ('error' in reminderResult) {
      showErrorToast(reminderResult.error);
      return;
    }

    setLoading(true);

    try {
      const response = await APIRoutes.createEventSeries({
        subject: subject.trim(),
        description: description.trim(),
        start: startISO,
        end: endISO,
        until,
        link: link.trim(),
        rule,
        featureFilter: buildEventFeatureFilter(isPublic, features, excludeCommunityMembers, onlyCommunityMembers),
        reminderMinutes: reminderResult.minutes
      });
      onSeriesCreated(response.series, response.events);
      onClose();
      resetForm();
    } catch (err) {
      console.error('Error creating event series:', err);
      // The backend reports the occurrence cap and every remaining rule problem as
      // INVALID_INPUT with an English message, so name the likely cause ourselves
      showErrorToast(getUserFacingError(err, 'Fehler beim Erstellen der Serie', {
        INVALID_INPUT: 'Die Serie konnte nicht erzeugt werden. Bitte prüfen Sie Rhythmus und Serienende — eine Serie darf höchstens 500 Termine umfassen.'
      }));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      resetForm();
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Neue Event-Serie erstellen"
      icon={<FontAwesomeIcon icon={faRepeat} className="w-5 h-5 text-purple-600" />}
      maxWidth="2xl"
      closeDisabled={loading}
    >
      <form onSubmit={handleSubmit} className="p-6">
        <div className="space-y-4">
          {/* Subject */}
          <div>
            <label htmlFor="seriesSubject" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Betreff *
            </label>
            <input
              type="text"
              id="seriesSubject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
              placeholder="z.B. Mittags-Q&A"
              disabled={loading}
              autoFocus
            />
          </div>

          {/* Description */}
          <div>
            <label htmlFor="seriesDescription" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Beschreibung
            </label>
            <textarea
              id="seriesDescription"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
              placeholder="Optionale Beschreibung der Serie..."
              disabled={loading}
            />
          </div>

          {/* Start date + time window */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="seriesStartDate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Serienstart *
              </label>
              <input
                type="date"
                id="seriesStartDate"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
                disabled={loading}
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Ab hier wird gesucht — der erste Termin ist das erste Vorkommen, das zur Regel passt.
              </p>
            </div>

            <div>
              <label htmlFor="seriesStartTime" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Uhrzeit *
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="time"
                  id="seriesStartTime"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
                  disabled={loading}
                />
                <span className="text-sm text-gray-500 dark:text-gray-400">bis</span>
                <input
                  type="time"
                  id="seriesEndTime"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
                  disabled={loading}
                />
              </div>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Gilt für jeden Termin der Serie.
              </p>
            </div>
          </div>

          {/* Until */}
          <div>
            <label htmlFor="seriesUntil" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Serienende *
            </label>
            <input
              type="date"
              id="seriesUntil"
              value={until}
              onChange={(e) => setUntil(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
              disabled={loading}
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Letzter Tag, an dem noch ein Termin erzeugt wird (einschließlich).
            </p>
          </div>

          {/* Link */}
          <div>
            <label htmlFor="seriesLink" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Link *
            </label>
            <input
              type="url"
              id="seriesLink"
              value={link}
              onChange={(e) => setLink(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
              placeholder="https://..."
              disabled={loading}
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Externer Link, der für alle Termine der Serie gilt (z.B. Google Meet).
            </p>
          </div>

          <RecurrenceRuleEditor rule={rule} onChange={setRule} until={until} disabled={loading} />

          <EventFeatureFilterEditor
            isPublic={isPublic}
            onIsPublicChange={setIsPublic}
            features={features}
            onFeaturesChange={setFeatures}
            excludeCommunityMembers={excludeCommunityMembers}
            onExcludeCommunityMembersChange={setExcludeCommunityMembers}
            onlyCommunityMembers={onlyCommunityMembers}
            onOnlyCommunityMembersChange={setOnlyCommunityMembers}
            disabled={loading}
            entityLabel="Serie"
          />

          <ReminderMinutesEditor rows={reminderRows} onChange={setReminderRows} disabled={loading} />
        </div>

        <div className="flex justify-end space-x-3 mt-6">
          <button
            type="button"
            onClick={handleClose}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-600 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-300 dark:hover:bg-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 cursor-pointer"
          >
            Abbrechen
          </button>
          <button
            type="submit"
            disabled={loading || !subject.trim() || !startDate || !startTime || !endTime || !until || !link.trim() || (!isPublic && features.length === 0)}
            className="px-4 py-2 text-sm font-medium text-white bg-purple-600 border border-transparent rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 cursor-pointer flex items-center"
          >
            {loading ? (
              <>
                <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 mr-2 animate-spin" />
                Erstellen...
              </>
            ) : (
              'Serie erstellen'
            )}
          </button>
        </div>
      </form>
    </Modal>
  );
}
